import {
  IColorByValueAttributeMap,
  IColorByValueAttributeSegmentColorProfile
} from 'app/services/api/colorByValue'

import { generateColorRangeForStringType } from './cbvColorMapGenerator'
import { getHexCodesFromCBVTheme } from './cbvThemes'

const getStringValueKey = (
  value: IColorByValueAttributeSegmentColorProfile['value']['string']
) => {
  if (value == null) {
    return ''
  }

  return String(value.id ?? '').toLowerCase()
}

const getFallbackValueKey = (
  value: IColorByValueAttributeSegmentColorProfile['value']['string']
) => {
  if (value == null) {
    return ''
  }

  return String(value.code ?? '').toLowerCase()
}

const getKeyCounts = (
  configurations: IColorByValueAttributeSegmentColorProfile[],
  getKey: (
    value: IColorByValueAttributeSegmentColorProfile['value']['string']
  ) => string
) => {
  const keyCount = new Map<string, number>()

  for (const config of configurations) {
    const key = getKey(config.value.string)

    if (!key) {
      continue
    }

    keyCount.set(key, (keyCount.get(key) ?? 0) + 1)
  }

  return keyCount
}

const reconcileStringConfigurations = (
  existingConfigurations: IColorByValueAttributeSegmentColorProfile[],
  stringSet: NonNullable<
    IColorByValueAttributeMap['attributeTypeRange']['string']
  >
) => {
  const defaultConfigurations = generateColorRangeForStringType(
    getHexCodesFromCBVTheme('lighthouse'),
    stringSet
  )

  const existingIdKeyCounts = getKeyCounts(
    existingConfigurations,
    getStringValueKey
  )
  const defaultIdKeyCounts = getKeyCounts(
    defaultConfigurations,
    getStringValueKey
  )

  const existingColorById = new Map<string, string>()
  const existingColorByCode = new Map<string, string>()

  for (const config of existingConfigurations) {
    const stringValue = config.value.string

    if (stringValue == null) {
      continue
    }

    const primaryKey = getStringValueKey(stringValue)
    const fallbackKey = getFallbackValueKey(stringValue)

    if (
      primaryKey.length > 0 &&
      existingIdKeyCounts.get(primaryKey) === 1 &&
      !existingColorById.has(primaryKey)
    ) {
      existingColorById.set(primaryKey, config.color)
    }

    if (fallbackKey.length > 0 && !existingColorByCode.has(fallbackKey)) {
      existingColorByCode.set(fallbackKey, config.color)
    }
  }

  // Use existing CBV color if coldef row exists in config, otherwise use default lighthouse CBV config
  return defaultConfigurations.map((config) => {
    const stringValue = config.value.string
    const primaryKey = getStringValueKey(stringValue)
    const fallbackKey = getFallbackValueKey(stringValue)

    const canUsePrimaryKey =
      primaryKey.length > 0 && defaultIdKeyCounts.get(primaryKey) === 1

    const existingColor =
      (canUsePrimaryKey ? existingColorById.get(primaryKey) : undefined) ??
      existingColorByCode.get(fallbackKey)

    return {
      ...config,
      color: existingColor ?? config.color
    }
  })
}

export { reconcileStringConfigurations }
